import React, { useCallback, useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useSelector } from "react-redux";
import axios from "axios";
import { List } from "../../component/list/list";
import { useNavigate } from "react-router-dom";
import ModalManage from "../QuestionsView/list/components/ModalManage";

interface Question {
  id: string;
  question_type: string;
  organization_name: string;
  user_name: string;
  statement: string;
}

export function QuestionsPublicView() {
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [showModalManage, setShowModalManage] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [deleteQuestionId, setDeleteQuestionId] = useState<string | null>(null);

  const navigate = useNavigate();
  const token = useSelector((state: any) => state.auth.token);

  const fetchQuestions = useCallback(
    async (page: number, searchQuery: string) => {
      setLoadingSearch(true);
      try {
        const activeUrl = searchQuery
          ? `${process.env.REACT_APP_API_URI}/admin/private/user-questions?page=${page}&search=${searchQuery}`
          : `${process.env.REACT_APP_API_URI}/admin/private/user-questions?page=${page}`;

        const expiredUrl = searchQuery
          ? `${process.env.REACT_APP_API_URI}/admin/private/user-questions/expired?page=${page}&search=${searchQuery}`
          : `${process.env.REACT_APP_API_URI}/admin/private/user-questions/expired?page=${page}`;

        const [activeResponse, expiredResponse] = await Promise.all([
          axios.get(activeUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
          axios.get(expiredUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
        ]);

        const uniqueQuestionsMap = new Map();
        
        activeResponse.data.data.forEach((question: any) => {
          uniqueQuestionsMap.set(question.id, question);
        });
        
        expiredResponse.data.data.forEach((question: any) => {
          if (!uniqueQuestionsMap.has(question.id)) {
            uniqueQuestionsMap.set(question.id, question);
          }
        });

        const combinedQuestions = Array.from(uniqueQuestionsMap.values());

        const questions_new = combinedQuestions.map((item: any) => {
          let new_item = { ...item };
          new_item.question_type =
            new_item.question_type === "objective"
              ? "Objetiva"
              : new_item.question_type === "dissertation" ||
                new_item.question_type === "text"
              ? "Dissertativa"
              : new_item.question_type === "set"
              ? "Conjunto"
              : new_item.question_type;
          new_item.statement =
            new_item.statement.length > 30
              ? new_item.statement.substring(0, 30) + "..."
              : new_item.statement;
          return new_item;
        });

        setQuestions(questions_new);
        setLastPage(Math.max(activeResponse.data.total_pages, expiredResponse.data.total_pages));
      } catch (error) {
        console.error("Error fetching questions:", error);
      } finally {
        setLoadingSearch(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (!search.trim()) {
      fetchQuestions(currentPage, "");
    }
  }, [currentPage, fetchQuestions, search]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (value.trim() === "") {
      fetchQuestions(1, "");
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchQuestions(1, value);
    }, 500);

    setSearchTimeout(timeoutId);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleCloseModalManage = () => setShowModalManage(false);

  const handleViewQuestion = (id: string) => {
    setSelectedQuestionId(id);
    setShowModalManage(true);
  };

  const handleDeleteQuestion = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URI}/admin/private/user-questions/${deleteQuestionId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setShowDeleteWarning(false);
      setDeleteQuestionId(null);
      fetchQuestions(1, ''); 
    } catch (error) {
      console.error('Error deleting question:', error);
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeleteQuestionId(id);
    setShowDeleteWarning(true);
  };

  const handleSearchButtonClick = () => {
  };

  return (
    <div className="row">
      <List
        onChangeSearchInput={handleSearch}
        search_input_value={search}
        title={"Questões Públicas"}
        search_text="Pesquisar questões"
        not_found_message={"Nenhuma questão encontrada"}
        onChangePagination={(value: number) => {
          handlePageChange(value);
        }}
        show_warning={showDeleteWarning}
        deleting_message="Tem certeza que deseja excluir esta questão?"
        onDeleteCancel={() => setShowDeleteWarning(false)}
        onDeleteConfirm={handleDeleteQuestion}
        onManagerActionClick={(id) => handleViewQuestion(id)}
        onEditActionClick={(id) => navigate(`/public-questions/edit/${id}`)} 
        onDeleteActionClick={handleDeleteClick}
        hideEdit={false}
        hideDelete={false}
        pronoun="a"
        loading_screen={loadingSearch}
        loading_search={loadingSearch}
        items={questions}
        pagination={{ last_page: lastPage, current_page: currentPage }}
        fields={[
          { header: "ID", name: "id", type: "text" },
          { header: "Tipo", name: "question_type", type: "text" },
          { header: "Enunciado", name: "statement", type: "text" },
          { header: "Organização", name: "organization_name", type: "text" },
          { header: "Usuário", name: "user_name", type: "text" },
        ]}
        onSearchButtonClick={handleSearchButtonClick}
      />

      <ModalManage
        id={selectedQuestionId}
        token={token}
        showModalManage={showModalManage}
        setShowModalManage={handleCloseModalManage}
      />
    </div>
  );
}

export default QuestionsPublicView;
